import React from 'react';
import { Box, ImageList, Paper, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ImagesGroupQcFieldProps } from '50.quickConnect.Fields/FieldsTypes/Containers/ImagesGroupQcField/types';
import { ImagesGroupDesc } from '90.quickConnect.Models/models';
import useData from '50.quickConnect.Fields/FieldsTypes/Containers/ImagesGroupQcField/hooks';
import ImageGroupItem from '50.quickConnect.Fields/FieldsTypes/Containers/ImagesGroupQcField/components/ImageGroupItem';
import { FieldMainBox, FlexRow } from '50.quickConnect.Fields/styles';
import QcTooltip from '50.quickConnect.Fields/QcTooltip';
import { useIsMobile } from '80.quickConnect.Core/hooks';
import { ImagesGroupProvider } from '50.quickConnect.Fields/FieldsTypes/Containers/ImagesGroupQcField/contexts/imagesGroupContext';
import { ModalState } from '50.quickConnect.Fields/FieldsTypes/Containers/ImagesGroupQcField/components/Modals/ModalState';
import FieldRenderer from '50.quickConnect.Fields/Fields/FieldRenderer';
import MandatoryField from '10.quickConnect.app/components/domain/Home/Shared/MandatoryFields';
import getFieldKey from '50.quickConnect.Fields/helpers/getFieldKey';
import { C2SImportant } from '50.quickConnect.Fields/Fields/styles';
import DialogSummary from '50.quickConnect.Fields/FieldsTypes/Containers/DialogQcField/DialogSummary';
import {
  CustomAccordion,
  CustomAccordionDetails,
  CustomAccordionSummary,
} from '50.quickConnect.Fields/FieldsTypes/Containers/GroupQcField/styles';

export default function ImagesGroupQcField(imagesGroupQcFieldProps: ImagesGroupQcFieldProps) {
  const { field, updateDeclaration, labelClass, labelSpan, flattenFields, isReadOnly } = imagesGroupQcFieldProps;
  const imagesGroupField = field as ImagesGroupDesc;
  const { fullPathId, toolTip, id, summary, items } = imagesGroupField;

  // Business Logic
  const {
    imagesList,
    imagesGroupContextValue,
    imagesGroupState: { fieldToShow },
    handleCancelButtonClick,
    handleValidateButtonClick,
    isExpanded,
    handleChange,
  } = useData(imagesGroupField, updateDeclaration, flattenFields, isReadOnly);
  const isMobile = useIsMobile();

  const customTheme = useTheme();
  const { breakpoints } = customTheme;

  // Exclure labelSpan des props passées à FieldRenderer
  const { labelSpan: unusedLabelSpan, ...restProps } = imagesGroupQcFieldProps;
  return (
    <Box className={labelClass} sx={FieldMainBox(breakpoints)} id={`scrollToAnchor-${fullPathId}`}>
      <CustomAccordion
        theme={customTheme}
        expanded={isExpanded}
        onChange={() => handleChange(isExpanded)}
        elevation={0}
      >
        <CustomAccordionSummary
          theme={customTheme}
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel-imagesgroup-${id}`}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', padding: '6px 7px 6px 0px', width: '100%' }}>
            <Box sx={FlexRow}>
              <Box component="span" sx={{ flex: 1 }}>
                {labelSpan}
              </Box>
              {toolTip && <QcTooltip parentId={id} tooltip={toolTip} />}
            </Box>
          </Box>
        </CustomAccordionSummary>

        <CustomAccordionDetails theme={customTheme}>
          <Paper
            elevation={1}
            sx={{
              padding: 2,
            }}
          >
            <ImagesGroupProvider value={imagesGroupContextValue}>
              <ImageList cols={isMobile ? 1 : 3} sx={{ padding: 1 }} rowHeight={150}>
                {/* Affichage de la grille */}
                {imagesList.length > 0 &&
                  imagesList.map(([fieldId, url]: [string, string], index: number) => (
                    <ImageGroupItem
                      key={`${imagesGroupField.fullPathId}-${fieldId}-${index.toString()}`}
                      fieldId={fieldId}
                      fullPathId={`${fullPathId}.${fieldId}`}
                      labelClass={labelClass}
                      url={url}
                    />
                  ))}
              </ImageList>
              {fieldToShow && (
                <ModalState
                  field={fieldToShow}
                  isOpen={!!fieldToShow}
                  handleValidateButtonClick={handleValidateButtonClick}
                  handleCancelButtonClick={handleCancelButtonClick}
                >
                  {fieldToShow.importance && fieldToShow?.visible === true ? (
                    <Box sx={C2SImportant(fieldToShow.importance)}>
                      <MandatoryField key={getFieldKey(fieldToShow)} fieldDesc={fieldToShow}>
                        <FieldRenderer {...restProps} field={fieldToShow} openByImagesGroupField />
                      </MandatoryField>
                    </Box>
                  ) : (
                    <MandatoryField key={getFieldKey(fieldToShow)} fieldDesc={fieldToShow}>
                      <FieldRenderer {...restProps} field={fieldToShow} openByImagesGroupField />
                    </MandatoryField>
                  )}
                </ModalState>
              )}
            </ImagesGroupProvider>
          </Paper>
        </CustomAccordionDetails>
      </CustomAccordion>
      {/* Affichage résumé tt le temps, et non seulement quand replié comme groupe */}
      {summary === true && <DialogSummary parentId={fullPathId} items={[...items]} isReadOnly={isReadOnly} />}
    </Box>
  );
}
